import { Html, Line } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useRef, useState, useEffect } from 'react';
import { Vector3, Group, DoubleSide } from 'three';

import styles from './styles.module.scss';

// Animation timing constants (in seconds)
const CIRCLE_FADE_DURATION = 0.2;
const RING_START_DELAY = 0.2; // Start after circle
const RING_FADE_DURATION = 0.3;
const LINE_START_DELAY = RING_START_DELAY + RING_FADE_DURATION + 0.1; // Start after ring
const LINE_FADE_DURATION = 0.3;
const HTML_START_DELAY = LINE_START_DELAY + LINE_FADE_DURATION + 0.1; // Start after line
const HTML_FADE_DURATION = 0.1;

// Define annotation points for each stage
interface Annotation {
	position: Vector3;
	labelHeading: string;
	labelBody: React.ReactNode;
	labelOffset?: [number, number, number]; // Optional label offset
	lineStartOffset?: [number, number, number]; // Optional line start offset from annotation center
}

// Keyframes for annotations at each stage
export const ANNOTATION_KEY_FRAMES: Annotation[] = [
	{
		position: new Vector3(-0.25, 0.32, -0.3),
		labelHeading: 'Personalized Arch Support',
		labelBody: <>Each insole is assigned personalized arch support to match the unique measurements of your feet</>,
		labelOffset: [-0.5, 0.4, 0.3], // Default offset
		lineStartOffset: [0, 0, 0] // Default line start
	},
	{
		position: new Vector3(0.7, 1.30, 0.4),
		labelHeading: 'Stability Ribs',
		labelBody: <>Stability ribs are scaled for thickness based on body mass to optimize support for you</>,
		labelOffset: [1.2, 0.2, 0.5], // Default offset
		lineStartOffset: [0, 0, 0] // Default line start
	},
	{
		position: new Vector3(-0.9, 2, 0.45),
		labelHeading: 'Heel Pad',
		labelBody: <>The heel pad creates a more flexible and cushioned underfoot feel</>,
		labelOffset: [-1.2, -0.05, 0.5], // Adjusted for lattice view - move label to the left and up
		lineStartOffset: [0.0, 0.0, 0] // Default line start
	},
	{
		position: new Vector3(0.2, 2, 0.7),
		labelHeading: 'Warp Knit Top Cover',
		labelBody: <>Recycled, durable Warp Knit top cover with Moisturewick™ technology to reduce odors</>,
		labelOffset: [0.9, 0.5, 0.5], // Default offset
		lineStartOffset: [0, 0, 0] // Default line start
	},
];

interface ArrowProps {
	position: Vector3;
	labelHeading: string;
	labelBody: React.ReactNode;
	labelOffset?: [number, number, number]; // Optional label offset from annotation point
	lineStartOffset?: [number, number, number]; // Optional line start offset
	startDelay?: number; // Delay before starting the animation (in seconds)
}

const Arrow = ({ position, labelHeading, labelBody, labelOffset = [0.5, 0.5, 0.3], lineStartOffset = [0.03, 0.03, 0], startDelay = 1 }: ArrowProps) => {
	const { invalidate } = useThree();
	const groupRef = useRef<Group>(null);
	const [hovered, setHovered] = useState(false);
	const animationStartTime = useRef<number | null>(null);
	const [ringOpacity, setRingOpacity] = useState(0);
	const [circleOpacity, setCircleOpacity] = useState(0);
	const [lineOpacity, setLineOpacity] = useState(0);
	const [lineProgress, setLineProgress] = useState(0); // 0 to 1 for line length animation
	const [htmlOpacity, setHtmlOpacity] = useState(0);

	// Reset animation when component mounts or position changes
	useEffect(() => {
		animationStartTime.current = null;
		setRingOpacity(0);
		setCircleOpacity(0);
		setLineOpacity(0);
		setLineProgress(0);
		setHtmlOpacity(0);
	}, [position]);

	useFrame(({ camera, clock }) => {
		// Make arrow always face the camera
		if (groupRef.current) {
			groupRef.current.lookAt(camera.position);
		}

		// Initialize animation start time
		if (animationStartTime.current === null) {
			animationStartTime.current = clock.elapsedTime;
		}

		const elapsed = clock.elapsedTime - animationStartTime.current;

		// 1. Circle fades in first (after startDelay)
		if (elapsed >= startDelay) {
			const circleProgress = Math.min((elapsed - startDelay) / CIRCLE_FADE_DURATION, 1);
			setCircleOpacity(circleProgress);
		}

		// 2. Ring fades in after circle
		const ringStartTime = startDelay + RING_START_DELAY;
		if (elapsed >= ringStartTime) {
			const ringProgress = Math.min((elapsed - ringStartTime) / RING_FADE_DURATION, 1);
			setRingOpacity(ringProgress);
		}

		// 3. Line fades in and grows after circle
		const lineStartTime = startDelay + LINE_START_DELAY;
		if (elapsed >= lineStartTime) {
			const progress = Math.min((elapsed - lineStartTime) / LINE_FADE_DURATION, 1);
			// Use easeOutCubic for smooth animation
			const easedProgress = 1 - Math.pow(1 - progress, 3);
			setLineOpacity(progress);
			setLineProgress(easedProgress);
		}

		// 4. HTML label fades in after line
		const htmlStartTime = startDelay + HTML_START_DELAY;
		if (elapsed >= htmlStartTime) {
			const htmlProgress = Math.min((elapsed - htmlStartTime) / HTML_FADE_DURATION, 1);
			setHtmlOpacity(htmlProgress);
		}

		// Self-sustain frames until the full animation sequence is complete
		if (htmlOpacity < 1) {
			invalidate();
		}
	});

	// Animated radius values
	const ringRadius = ringOpacity * 0.1; // Scale from 0 to 0.1
	const ringInnerRadius = ringOpacity * 0.045; // Scale from 0 to 0.045
	const circleRadius = circleOpacity * 0.03; // Scale from 0 to 0.03

	// Animated line points - line grows from start to end
	const animatedLinePoints = [
		new Vector3(...lineStartOffset),
		new Vector3(
			lineStartOffset[0] + (labelOffset[0] - lineStartOffset[0]) * lineProgress,
			lineStartOffset[1] + (labelOffset[1] - lineStartOffset[1]) * lineProgress,
			lineStartOffset[2] + (labelOffset[2] - lineStartOffset[2]) * lineProgress
		)
	];

	return (
		<group ref={groupRef} position={position}>
			{/* Outer ring - appears first */}
			{ringOpacity > 0 && (
				<mesh>
					<ringGeometry args={[ringRadius, ringInnerRadius, 64]} />
					<meshBasicMaterial color="#000000" side={DoubleSide} transparent opacity={ringOpacity * 0.5} />
				</mesh>
			)}

			{/* Inner circle - appears second */}
			{circleOpacity > 0 && (
				<mesh>
					<circleGeometry args={[circleRadius, 64]} />
					<meshBasicMaterial color="#BAFF4F" transparent opacity={circleOpacity} />
				</mesh>
			)}

			{/* Line connecting ringGeometry to HTML label - appears third and grows */}
			{lineOpacity > 0 && (
				<Line
					points={animatedLinePoints}
					lineWidth={1}
					color="#000000"
					dashed={false}
					transparent
					opacity={lineOpacity}
				/>
			)}

			{/* Label - appears last */}
			{htmlOpacity > 0 && (
				<Html
					position={labelOffset}
					center
					distanceFactor={2}
					style={{
						pointerEvents: 'auto',
						userSelect: 'none',
						transition: 'all 0.2s',
						opacity: htmlOpacity,
					}}
				>
					<div
						onPointerEnter={() => setHovered(true)}
						onPointerLeave={() => setHovered(false)}
						className={styles.annotationLabel}
						style={{
							background: hovered ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.9)',
							transform: hovered ? 'scale(1.1)' : 'scale(1)',
						}}
					>
						<div className={styles.annotationLabelHeading}>
							{labelHeading}
						</div>
						<div className={styles.annotationLabelBody}>
							{labelBody}
						</div>
					</div>
				</Html>
			)}
		</group>
	);
};

interface AnnotationArrowsProps {
	stageRef: React.MutableRefObject<number>;
	startDelay?: number; // Delay before starting the animation (in seconds)
}

export const AnnotationArrows = ({ stageRef, startDelay = 1.7 }: AnnotationArrowsProps) => {
	const [stage, setStage] = useState(stageRef.current);

  useFrame(() => {
    if (stage !== stageRef.current) {
      setStage(stageRef.current);
    }
  });

	// Get annotations for current stage with bounds checking
	const currentAnnotations = ANNOTATION_KEY_FRAMES[stage];

	// Don't render if no annotation for this stage
	if (!currentAnnotations) return null;

	return (
		<Arrow
			position={currentAnnotations.position}
			labelHeading={currentAnnotations.labelHeading}
			labelBody={currentAnnotations.labelBody}
			labelOffset={currentAnnotations.labelOffset}
			lineStartOffset={currentAnnotations.lineStartOffset}
			startDelay={startDelay}
		/>
	);
};
